import { linkChargingStation } from '@infuse/api/e-connector/charging-station';
import { useNotify } from '@infuse/cpms/ui';
import { LinkedCentralSystemType } from '@infuse/e-connector/central-system/domain';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { CentralSystemLinkUpsert } from './useBulkUpsertCentralSystemLinks';
import { createLinkRequestFor } from './useUpsertCentralSystemLink';

export const useSwapMasterCentralSystemLink = () => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyFailure } = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  return useMutation({
    mutationFn: async ({
      newMaster,
      currentMaster
    }: {
      newMaster: CentralSystemLinkUpsert;
      currentMaster?: CentralSystemLinkUpsert;
    }) => {
      // demote first: only one MASTER is allowed per charging station
      if (currentMaster) {
        await linkChargingStation(
          { id: currentMaster.linkedCentralSystem.chargingStationId },
          createLinkRequestFor(
            { ...currentMaster.linkedCentralSystem, type: LinkedCentralSystemType.READONLY },
            currentMaster.centralSystemName,
            false
          )
        );
      }

      return linkChargingStation(
        { id: newMaster.linkedCentralSystem.chargingStationId },
        createLinkRequestFor(
          { ...newMaster.linkedCentralSystem, type: LinkedCentralSystemType.MASTER },
          newMaster.centralSystemName,
          false
        )
      );
    },
    onSuccess: async (_, { newMaster }) => {
      await queryClient.invalidateQueries({ queryKey: ['charging-stations'] });

      notifySuccess({
        title: t('e_connector.common.notify.success'),
        message: t('e_connector.chargingStationLinks.notify.makeMaster.success', {
          name: newMaster.centralSystemName
        })
      });
    },
    onError: async (error) => {
      // the demote may have succeeded before the promote failed, so refetch either way
      await queryClient.invalidateQueries({ queryKey: ['charging-stations'] });

      notifyFailure({
        title: t('e_connector.common.notify.error'),
        message: t('e_connector.chargingStationLinks.notify.makeMaster.error', {
          reason: getAxiosErrorMessage(error, '')
        })
      });
    }
  });
};
